import Stripe from "stripe";

let _stripe: Stripe | null = null;

export function getStripe(): Stripe {
  if (!process.env.STRIPE_SECRET_KEY) {
    throw new Error("STRIPE_SECRET_KEY is not set");
  }
  if (!_stripe) {
    _stripe = new Stripe(process.env.STRIPE_SECRET_KEY, {
      apiVersion: "2026-02-25.clover",
      typescript: true,
    });
  }
  return _stripe;
}

// Keep backward compat — lazy proxy
export const stripe = new Proxy({} as Stripe, {
  get(_target, prop) {
    return getStripe()[prop as keyof Stripe];
  },
});

// ── Plan types ──────────────────────────────────────────────────────────────

export interface PlanConfig {
  id: string;
  name: string;
  price: string;
  priceMonthly: number | null;
  description: string;
  videos: number;
  images: number;
  brands: number;
  users: number;
  exportsPerMonth: number;       // Infinity = unlimited
  socialAccounts: number;        // Infinity = unlimited
  generationsPerDay: number;     // Infinity = unlimited
  postsPerDay: number;           // Infinity = unlimited
  repostsPerDay: number;         // Infinity = unlimited
  trialDays: number;
  canPublish: boolean;
  editorDesignsPerMonth: number;  // Infinity = unlimited
  socialAuditFrequency: "weekly" | "monthly" | null;
  visible: boolean;
  sortOrder: number;
  stripePriceId: string | null;
}

export type PlanKey = "free" | "trial" | "growth" | "agency" | "scale";

// ── Hardcoded fallback (used if DB fetch fails) ─────────────────────────────

const FALLBACK_PLANS: Record<string, PlanConfig> = {
  free: {
    id: "free", name: "Free Trial", price: "$0", priceMonthly: 0,
    description: "14-day free trial to explore the platform",
    videos: 3, images: 10, brands: 1, users: 1,
    exportsPerMonth: 5, socialAccounts: 2, generationsPerDay: 3,
    postsPerDay: Infinity, repostsPerDay: Infinity, trialDays: 14,
    canPublish: true, editorDesignsPerMonth: Infinity, socialAuditFrequency: null,
    visible: true, sortOrder: 0, stripePriceId: null,
  },
  growth: {
    id: "growth", name: "Growth", price: "$299", priceMonthly: 299,
    description: "For individual creators and small brands",
    videos: 25, images: 200, brands: 1, users: 1,
    exportsPerMonth: Infinity, socialAccounts: Infinity, generationsPerDay: Infinity,
    postsPerDay: Infinity, repostsPerDay: Infinity, trialDays: 0,
    canPublish: true, editorDesignsPerMonth: Infinity, socialAuditFrequency: "monthly",
    visible: true, sortOrder: 1, stripePriceId: process.env.STRIPE_PRICE_GROWTH_MONTHLY || null,
  },
  agency: {
    id: "agency", name: "Agency", price: "$599", priceMonthly: 599,
    description: "For growing teams with multiple brands",
    videos: 50, images: 500, brands: 3, users: 3,
    exportsPerMonth: Infinity, socialAccounts: Infinity, generationsPerDay: Infinity,
    postsPerDay: Infinity, repostsPerDay: Infinity, trialDays: 0,
    canPublish: true, editorDesignsPerMonth: Infinity, socialAuditFrequency: "weekly",
    visible: true, sortOrder: 2, stripePriceId: process.env.STRIPE_PRICE_AGENCY_MONTHLY || null,
  },
  // NOTE: social_accounts is Infinity (unlimited) for all paid plans — the publisher exposes 7 platforms,
  // capping at 3 would artificially block legitimate use. Free stays at 2 as trial constraint.
  scale: {
    id: "scale", name: "Scale", price: "Custom", priceMonthly: null,
    description: "Enterprise — unlimited everything",
    videos: Infinity, images: Infinity, brands: Infinity, users: Infinity,
    exportsPerMonth: Infinity, socialAccounts: Infinity, generationsPerDay: Infinity,
    postsPerDay: Infinity, repostsPerDay: Infinity, trialDays: 0,
    canPublish: true, editorDesignsPerMonth: Infinity, socialAuditFrequency: "weekly",
    visible: false, sortOrder: 3, stripePriceId: null,
  },
};
// NOTE: Plan IDs map to Stripe product tiers: free=trial, growth=$299, agency=$599, scale=enterprise.
// Historical rename (2026-04-17): old "creator" ID renamed to "growth", old "growth" ID renamed to "agency".

// ── DB → PlanConfig mapper ──────────────────────────────────────────────────

function dbRowToPlan(row: any): PlanConfig {
  return {
    id: row.id,
    name: row.name,
    price: row.price_label || (row.price_monthly > 0 ? `$${row.price_monthly}` : "$0"),
    priceMonthly: row.price_monthly ?? null,
    description: row.description || "",
    videos: row.videos_per_month ?? Infinity,
    images: row.images_per_month ?? Infinity,
    brands: row.brands_limit ?? Infinity,
    users: row.users_limit ?? Infinity,
    exportsPerMonth: row.exports_per_month ?? Infinity,
    socialAccounts: row.social_accounts ?? Infinity,
    generationsPerDay: row.generations_per_day ?? Infinity,
    postsPerDay: row.posts_per_day ?? Infinity,
    repostsPerDay: row.reposts_per_day ?? Infinity,
    trialDays: row.trial_days ?? 0,
    canPublish: row.can_publish ?? false,
    editorDesignsPerMonth: row.editor_designs_per_month ?? Infinity,
    socialAuditFrequency: row.social_audit_frequency || null,
    visible: row.visible ?? true,
    sortOrder: row.sort_order ?? 0,
    stripePriceId: row.stripe_price_id || null,
  };
}

// ── Cached plans from DB ────────────────────────────────────────────────────

let _plansCache: Record<string, PlanConfig> | null = null;
let _plansCacheExpires = 0;
const PLANS_CACHE_TTL = 60_000; // 1 minute

export async function fetchPlansFromDB(supabase: any): Promise<Record<string, PlanConfig>> {
  const now = Date.now();
  if (_plansCache && now < _plansCacheExpires) return _plansCache;

  try {
    const { data, error } = await supabase
      .from("plans")
      .select("*")
      .order("sort_order", { ascending: true });

    if (error || !data || data.length === 0) {
      console.warn("[stripe] Failed to fetch plans from DB, using fallback:", error?.message);
      return FALLBACK_PLANS;
    }

    const plans: Record<string, PlanConfig> = {};
    for (const row of data) {
      plans[row.id] = dbRowToPlan(row);
    }

    _plansCache = plans;
    _plansCacheExpires = now + PLANS_CACHE_TTL;
    return plans;
  } catch (err) {
    console.warn("[stripe] Plans fetch error, using fallback:", err);
    return FALLBACK_PLANS;
  }
}

// Invalidate cache (call after admin edits plans)
export function invalidatePlansCache() {
  _plansCache = null;
  _plansCacheExpires = 0;
}

// ── Sync getter (uses fallback if DB not fetched yet) ───────────────────────

// Backward compatibility: "trial" → "free" alias
const PLAN_ALIASES: Record<string, string> = { trial: "free" };

function resolvePlanKey(key: string): string {
  return PLAN_ALIASES[key] || key;
}

// For backward compatibility — components that can't await
// Includes "trial" alias pointing to "free"
export const PLANS: Record<string, PlanConfig> = {
  ...FALLBACK_PLANS,
  trial: FALLBACK_PLANS.free, // alias
};

export function getPlan(planKey: string | null | undefined): PlanConfig {
  const plans = _plansCache || FALLBACK_PLANS;
  const resolved = planKey ? resolvePlanKey(planKey) : "free";
  if (resolved in plans) return plans[resolved];
  return plans.free || FALLBACK_PLANS.free;
}

// ── API endpoint for client-side plan fetching ──────────────────────────────

export function plansToClientFormat(plans: Record<string, PlanConfig>) {
  return Object.values(plans)
    .filter(p => p.visible)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
